/**
 * ТЗ 0120 — компактная строка фильтров «Лидов» и сохранённые виды: чистые
 * помощники (без React, Apollo и i18n — их проверяют тесты). Общая часть —
 * в `_shared/savedViewsModel` (ключи реестра подставляются здесь).
 *
 * Строка фильтров: поиск, «Ответственный», быстрые чипы (высокий шанс,
 * контакт просрочен, сегодня), «Фильтры (N)» — всё остальное в поповере,
 * «Виды». Скрытые в поповере активные фильтры видны крошками под строкой.
 */

import {
  activeHiddenFilters,
  clearHiddenFilter,
  filtersToViewJson,
  sameFiltersBy,
  viewJsonRecord,
  viewJsonString as asString,
  viewJsonStrings as asStrings,
} from '../_shared/savedViewsModel';
import {
  EMPTY_LEADS_FILTERS,
  ILeadsFilters,
  LEAD_CHANCE_GOOD,
  LEAD_CHANCE_HIGH,
  LEAD_RESPONSIBLE_ME,
  LEAD_RESPONSIBLE_NONE,
  parseLeadsFilters,
  serializeLeadsFilters,
} from './leadsModel';
import { LeadsView } from './leadsBoardModel';

/**
 * Быстрые чипы строки фильтров. ТЗ 0127: «Хороший+» — шанс ≥ 55 % (высокие
 * тоже).
 */
export const LEAD_QUICK_FILTERS = ['hot', 'good', 'overdue', 'today'] as const;
export type LeadQuickFilter = typeof LEAD_QUICK_FILTERS[number];

/** Значение фильтра «Шанс» у чипа шанса. */
const CHIP_CHANCE: Partial<Record<LeadQuickFilter, string>> = {
  hot: LEAD_CHANCE_HIGH,
  good: LEAD_CHANCE_GOOD,
};

export const leadQuickPressed = (
  filters: Pick<ILeadsFilters, 'chance' | 'nextContact'>,
  chip: LeadQuickFilter,
): boolean => {
  const chance = CHIP_CHANCE[chip];
  if (chance) return filters.chance === chance;
  return filters.nextContact === chip;
};

/**
 * Нажатие чипа: «Высокий шанс» и «Хороший+» — значения одного фильтра
 * «Шанс», «Просрочен» и «Сегодня» — одного фильтра «Следующий контакт»
 * (второе нажатие снимает).
 */
export const toggleLeadQuick = (
  filters: Pick<ILeadsFilters, 'chance' | 'nextContact'>,
  chip: LeadQuickFilter,
): Partial<ILeadsFilters> => {
  const chance = CHIP_CHANCE[chip];
  if (chance) return { chance: filters.chance === chance ? '' : chance };
  return { nextContact: filters.nextContact === chip ? '' : chip };
};

/** Фильтры, которые живут в поповере «Фильтры» (а не в строке). */
export const LEAD_HIDDEN_FILTERS = [
  'ladder',
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'funding',
  'student',
  'category',
  'nextContact',
  'includeClosed',
  'created',
  'sort',
] as const;
export type LeadHiddenFilter = typeof LEAD_HIDDEN_FILTERS[number];

/**
 * Активен ли фильтр поповера. «Следующий контакт» скрыт, только когда это
 * «неделя»: «просрочен» и «сегодня» видны нажатым чипом.
 */
export const leadHiddenActive = (
  filters: ILeadsFilters,
  key: LeadHiddenFilter,
): boolean => {
  switch (key) {
    case 'utmSource':
    case 'utmMedium':
    case 'utmCampaign':
    case 'category':
      return filters[key].length > 0;
    case 'includeClosed':
      return filters.includeClosed;
    case 'nextContact':
      return filters.nextContact === 'week';
    default:
      return Boolean(filters[key]);
  }
};

/** Активные фильтры поповера — по порядку; их число — на кнопке «Фильтры». */
export const leadHiddenFilters = (filters: ILeadsFilters): LeadHiddenFilter[] =>
  activeHiddenFilters(LEAD_HIDDEN_FILTERS, filters, leadHiddenActive);

/** Снять один фильтр поповера (крошка ✕). */
export const clearLeadHidden = (
  key: LeadHiddenFilter,
): Partial<ILeadsFilters> => clearHiddenFilter(EMPTY_LEADS_FILTERS, key);

/* ── Сохранённые виды ───────────────────────────────────────────────── */

/** Ключи фильтра, которые хранит вид (белый список сервера). */
export const LEAD_VIEW_FILTER_KEYS = [
  'stages',
  'responsible',
  'search',
  'includeClosed',
  'ladder',
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'funding',
  'student',
  'sort',
  'created',
  'chance',
  'nextContact',
  'category',
] as const;
type ViewFilterKey = typeof LEAD_VIEW_FILTER_KEYS[number];

/** Фильтры → JSON вида: только непустые ключи белого списка. */
export const leadsFiltersToView = (
  filters: ILeadsFilters,
): Partial<Pick<ILeadsFilters, ViewFilterKey>> =>
  filtersToViewJson(LEAD_VIEW_FILTER_KEYS, filters);

/**
 * JSON вида (чужая запись, старая версия) → фильтры: каждое значение
 * проходит тот же разбор, что адрес, — мусор отбрасывается.
 */
export const leadsFiltersFromView = (raw: unknown): ILeadsFilters => {
  const record = viewJsonRecord(raw);
  const draft: ILeadsFilters = {
    ...EMPTY_LEADS_FILTERS,
    stages: asStrings(record.stages),
    responsible: asString(record.responsible),
    search: asString(record.search),
    includeClosed: record.includeClosed === true,
    ladder: asString(record.ladder),
    utmSource: asStrings(record.utmSource),
    utmMedium: asStrings(record.utmMedium),
    utmCampaign: asStrings(record.utmCampaign),
    funding: asString(record.funding),
    student: asString(record.student),
    sort: asString(record.sort),
    created: asString(record.created),
    chance: asString(record.chance),
    nextContact: asString(record.nextContact),
    category: asStrings(record.category),
  };
  return parseLeadsFilters(serializeLeadsFilters('', draft));
};

/** Те же фильтры (страница не в счёт; порядок значений — тоже). */
export const sameLeadsFilters = (a: ILeadsFilters, b: ILeadsFilters): boolean =>
  sameFiltersBy(LEAD_VIEW_FILTER_KEYS, a, b);

/**
 * Адрес после выбора вида: фильтры вида целиком заменяют текущие, вид
 * доски/таблицы — из записи; карточка лида (`?lead=`) не трогается.
 */
export const applyLeadsView = (
  search: string,
  filters: ILeadsFilters,
  view: LeadsView,
): string => {
  const params = new URLSearchParams(
    serializeLeadsFilters(search, { ...filters, page: 0 }),
  );
  if (view === 'table') params.set('view', 'table');
  else params.delete('view');
  return params.toString();
};

/** Готовые виды — у всех, без сервера. */
export const LEAD_VIEW_PRESETS = [
  'myHot',
  'unassigned',
  'overdue',
  'recent',
] as const;
export type LeadViewPreset = typeof LEAD_VIEW_PRESETS[number];

export const leadViewPresetFilters = (
  preset: LeadViewPreset,
): ILeadsFilters => {
  switch (preset) {
    case 'myHot':
      return {
        ...EMPTY_LEADS_FILTERS,
        responsible: LEAD_RESPONSIBLE_ME,
        chance: LEAD_CHANCE_HIGH,
        sort: 'win_chance',
      };
    case 'unassigned':
      return { ...EMPTY_LEADS_FILTERS, responsible: LEAD_RESPONSIBLE_NONE };
    case 'overdue':
      return {
        ...EMPTY_LEADS_FILTERS,
        nextContact: 'overdue',
        sort: 'win_chance',
      };
    case 'recent':
    default:
      return { ...EMPTY_LEADS_FILTERS, created: '7d' };
  }
};
